import { type Ref, computed, onScopeDispose, shallowRef, watch } from 'vue'

import useAuthUser from '@/composables/useAuthUser'

import {
	type HotelReportType,
	hotelReportAccess,
} from '../services/hotelReportAccess'

export type {
	HotelReportType,
	HotelReportAccessStatus,
	HotelReportAccessMap,
} from '../services/hotelReportAccess'

/** Consumers share only live access state; the last consumer releases it. */
export function useHotelReportAccess(
	companyId: Ref<number | undefined>,
	type: HotelReportType = 'legacy'
) {
	const { user } = useAuthUser()
	const scope = shallowRef<ReturnType<typeof hotelReportAccess.acquire>>()
	watch(
		[companyId, () => user.value?.id],
		([company, actor]) => {
			scope.value?.release()
			scope.value =
				company && actor ? hotelReportAccess.acquire(actor, company) : undefined
		},
		{ immediate: true, flush: 'sync' }
	)
	onScopeDispose(() => scope.value?.release())
	const statuses = computed(() => scope.value?.entry.statuses.value ?? null)
	const allowed = computed(() => statuses.value?.[type]?.allowed === true)
	const checking = computed(() => scope.value?.entry.checking.value ?? false)
	const message = computed(
		() =>
			scope.value?.entry.error.value ||
			statuses.value?.[type]?.message ||
			(!companyId.value ?
				'Selecciona una empresa para revisar su acceso al reporte.'
			:	'Verificando acceso al reporte…')
	)
	async function refresh() {
		const current = scope.value
		await current?.refresh()
		return current === scope.value && allowed.value
	}
	return { statuses, allowed, checking, message, refresh }
}
